import { FlexBox, Text } from '@fable/components'
import { css, useTheme } from '@fable/theme'
import { statsColors } from '../../../lib/theme'
import { useStats } from '../../../lib/ctx/stats'
import { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { abbreviateMonth } from '../../../lib/utils'

export const StreakCalendarMonths = ({ inView }: { inView: boolean }) => {
  const { colors } = useTheme()
  const [selectedMonth, setMonth, stats, theme] = useStats((s) => [
    s.streakMonth,
    s.setStreakMonth,
    s.stats,
    s.theme,
  ])

  const months = stats?.streaks?.results.map((s) => s.month) || []
  const containerRef = useRef<HTMLDivElement>(null)
  const monthRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current && monthRef.current) {
      containerRef.current.scrollTo({
        left: monthRef.current.offsetLeft - 16,
      })
    }
  }, [])

  const getMonthBgColor = (isCurrentMonth: boolean) => {
    if (isCurrentMonth) return colors.uiWhite

    return theme.streakCalendar?.tabBg || colors.uiBlack40
  }

  return (
    <div
      className={css`
        width: 100%;
        overflow: hidden;
        width: calc(100% + 32px);
        transform: translateX(-16px);
      `}
    >
      <motion.div
        variants={{
          hidden: { opacity: 0, x: 50 },
          visible: { opacity: 1, x: 0 },
        }}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className={css`
          width: 100%;
        `}
      >
        <FlexBox
          ref={containerRef}
          className={css`
            gap: 6px;
            width: 100%;
            overflow-x: auto;
            padding: 0 16px;
            scrollbar-width: none;
            &::-webkit-scrollbar {
              display: none;
            }
          `}
          alignItems="center"
        >
          {months?.map((month, i) => {
            const monthIndex = i + 1
            const isCurrentMonth = monthIndex === selectedMonth

            return (
              <FlexBox
                key={i}
                data-testid="streak-calendar-month"
                ref={isCurrentMonth ? monthRef : null}
                role="button"
                className={css`
                  padding: 0 10px;
                  width: 69px;
                  height: 36px;
                  border-radius: 100px;
                  background-color: ${getMonthBgColor(isCurrentMonth)};
                  flex-shrink: 0;
                  cursor: pointer;
                `}
                alignItems="center"
                onClick={() => setMonth(monthIndex)}
              >
                <Text
                  type="body"
                  sizing="M"
                  weight="600"
                  color={
                    isCurrentMonth ? statsColors.green.bg200 : colors.uiWhite70
                  }
                >
                  {abbreviateMonth(month as any)}
                </Text>
              </FlexBox>
            )
          })}
        </FlexBox>
      </motion.div>
    </div>
  )
}
